import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react'

import { useLingui } from '@lingui/react/macro'
import { transparentize } from 'color2k'
import {
  AreaSeries,
  CandlestickSeries,
  createChart,
  createSeriesMarkers,
  type ISeriesMarkersPluginApi,
  CrosshairMode,
  HistogramSeries,
  type IChartApi,
  type IPriceLine,
  type ISeriesApi,
  type MouseEventParams,
  type Time,
  type UTCTimestamp,
} from 'lightweight-charts'

import { useTheme } from 'common/hooks/useTheme'

import { PriceChartTooltip } from './PriceChartTooltip'
import * as styledEl from './SimpleChartCanvas.styled'

import { CANDLE_DOWN_COLOR, CANDLE_UP_COLOR } from '../config/priceChart.constants'
import { formatPriceChartValue, getPriceChartPriceFormat } from '../lib/priceChart.utils'
import { attachExecutionMarkersToBars } from '../lib/priceChartExecutionMarker.utils'
import { getSimpleExecutionMarkers } from '../lib/priceChartSimpleExecutionMarkers.utils'
import { syncSimplePriceLines } from '../lib/priceChartSimpleReferenceLines.utils'
import { createPriceChartTimeFormatters } from '../lib/priceChartTime.utils'

import type { ChartTooltipData } from './PriceChartTooltip'
import type {
  Candle,
  ChartMetric,
  ChartType,
  PriceChartReferenceLine,
  PriceChartExecutionMarker,
} from '../lib/priceChart.types'

export interface SimpleChartCanvasProps {
  data: Candle[]
  executionMarkers?: PriceChartExecutionMarker[]
  onSelectPrice?: (price: number) => void
  referenceLines: PriceChartReferenceLine<number>[]
  chartType: ChartType
  metric: ChartMetric
  showTooltip: boolean
  locale: string
}

interface SimpleChartInstance {
  chart: IChartApi
  priceSeries: ISeriesApi<'Area' | 'Candlestick'>
  volumeSeries: ISeriesApi<'Histogram'>
  markers: ISeriesMarkersPluginApi<Time>
}

const EMPTY_EXECUTION_MARKERS: PriceChartExecutionMarker[] = []

// eslint-disable-next-line max-lines-per-function
export function SimpleChartCanvas({
  data,
  executionMarkers = EMPTY_EXECUTION_MARKERS,
  onSelectPrice,
  referenceLines,
  chartType,
  metric,
  showTooltip,
  locale,
}: SimpleChartCanvasProps): ReactNode {
  const theme = useTheme()
  const { primary, text } = theme
  const { t } = useLingui()
  const attachedMarkers = useMemo(() => attachExecutionMarkersToBars(executionMarkers, data), [executionMarkers, data])
  const attachedMarkersRef = useRef(attachedMarkers)
  useEffect(() => {
    attachedMarkersRef.current = attachedMarkers
  }, [attachedMarkers])
  const priceLinesRef = useRef(new Map<string, IPriceLine>())
  const onSelectPriceRef = useRef(onSelectPrice)
  useEffect(() => {
    onSelectPriceRef.current = onSelectPrice
  }, [onSelectPrice])
  const containerRef = useRef<HTMLDivElement>(null)
  const chartRef = useRef<SimpleChartInstance | null>(null)
  const [tooltip, setTooltip] = useState<ChartTooltipData>()

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const chart = createChart(container, {
      autoSize: true,
      crosshair: {
        horzLine: { labelVisible: false },
        mode: CrosshairMode.Magnet,
        vertLine: { labelVisible: false },
      },
      grid: { horzLines: { visible: false }, vertLines: { visible: false } },
      handleScroll: { horzTouchDrag: true, mouseWheel: false, pressedMouseMove: true, vertTouchDrag: false },
      handleScale: { axisPressedMouseMove: true, mouseWheel: true, pinch: true },
      layout: { background: { color: 'transparent' } },
      rightPriceScale: { borderVisible: false, scaleMargins: { bottom: 0.15, top: 0.2 } },
      timeScale: { borderVisible: false, fixLeftEdge: true, fixRightEdge: true, timeVisible: true },
    })
    const priceSeries =
      chartType === 'line'
        ? chart.addSeries(AreaSeries, { crosshairMarkerRadius: 4, lineWidth: 1, priceLineVisible: false })
        : chart.addSeries(CandlestickSeries, { borderVisible: false, priceLineVisible: false })
    const volumeSeries = chart.addSeries(HistogramSeries, {
      lastValueVisible: false,
      priceFormat: { type: 'volume' },
      priceLineVisible: false,
      priceScaleId: '',
    })
    volumeSeries.priceScale().applyOptions({ scaleMargins: { bottom: 0, top: 0.8 } })
    const markers = createSeriesMarkers(priceSeries)
    chartRef.current = { chart, priceSeries, volumeSeries, markers }

    const handleClick = (event: MouseEventParams<Time>): void => {
      if (!onSelectPriceRef.current) {
        handleCrosshairMove(event)
        return
      }
      if (!event.point) return
      const price = priceSeries.coordinateToPrice(event.point.y)
      if (price !== null && Number.isFinite(price) && price > 0) onSelectPriceRef.current(price)
    }
    const handleCrosshairMove = (event: MouseEventParams<Time>): void => {
      if (onSelectPriceRef.current) {
        setTooltip(undefined)
        return
      }
      const priceData = event.seriesData.get(priceSeries)
      const price =
        priceData && ('value' in priceData ? priceData.value : 'close' in priceData ? priceData.close : undefined)
      const volumeData = event.seriesData.get(volumeSeries)
      const volume = volumeData && 'value' in volumeData ? volumeData.value : undefined

      setTooltip(
        event.point && typeof event.time === 'number' && price !== undefined
          ? {
              price,
              ohlc:
                priceData && 'open' in priceData
                  ? { open: priceData.open, high: priceData.high, low: priceData.low, close: priceData.close }
                  : undefined,
              time: event.time,
              volume,
              executions: attachedMarkersRef.current.filter((marker) => marker.barTimestamp === event.time),
              x: event.point.x,
              y: event.point.y,
              chartWidth: container.clientWidth,
              chartHeight: container.clientHeight,
            }
          : undefined,
      )
    }

    chart.subscribeCrosshairMove(handleCrosshairMove)
    chart.subscribeClick(handleClick)

    const priceLines = priceLinesRef.current
    return () => {
      priceLines.clear()
      setTooltip(undefined)
      chart.unsubscribeCrosshairMove(handleCrosshairMove)
      chart.unsubscribeClick(handleClick)
      markers.detach()
      chart.remove()
      chartRef.current = null
    }
  }, [chartType])

  useEffect(() => {
    const instance = chartRef.current
    if (!instance) return

    instance.chart.applyOptions({ layout: { textColor: text } })
    instance.priceSeries.applyOptions(
      chartType === 'line'
        ? { lineColor: primary, topColor: transparentize(primary, 0.75), bottomColor: transparentize(primary, 1) }
        : {
            upColor: CANDLE_UP_COLOR,
            downColor: CANDLE_DOWN_COLOR,
            wickUpColor: CANDLE_UP_COLOR,
            wickDownColor: CANDLE_DOWN_COLOR,
          },
    )
    instance.volumeSeries.applyOptions({ color: transparentize(text, 0.75) })
  }, [chartType, primary, text])

  useEffect(() => {
    const instance = chartRef.current
    if (!instance) return

    const { chart, priceSeries, volumeSeries } = instance
    const priceFormat = getPriceChartPriceFormat(data)
    const { tickMarkFormatter, timeFormatter } = createPriceChartTimeFormatters(locale)
    chart.applyOptions({
      timeScale: { tickMarkFormatter },
      localization: {
        locale,
        timeFormatter,
        priceFormatter: (value: number) =>
          formatPriceChartValue(Math.abs(value) < priceFormat.minMove / 2 ? 0 : value, locale),
      },
    })
    priceSeries.applyOptions({ priceFormat })
    // Work around stale time-scale points in Lightweight Charts 5.2.1: https://github.com/tradingview/lightweight-charts/issues/2154
    priceSeries.setData([])
    priceSeries.setData(
      data.map((bar) =>
        chartType === 'line'
          ? { time: bar.timestamp as UTCTimestamp, value: bar.close }
          : { time: bar.timestamp as UTCTimestamp, open: bar.open, high: bar.high, low: bar.low, close: bar.close },
      ),
    )
    volumeSeries.setData(
      data.flatMap((bar) =>
        bar.volume === undefined ? [] : [{ time: bar.timestamp as UTCTimestamp, value: bar.volume }],
      ),
    )
    chart.timeScale().fitContent()
    setTooltip(undefined)
  }, [chartType, data, locale])

  useEffect(() => {
    const instance = chartRef.current
    if (!instance) return
    const selecting = !!onSelectPrice
    instance.chart.applyOptions({
      crosshair: {
        mode: selecting ? CrosshairMode.Normal : CrosshairMode.Magnet,
        horzLine: { labelVisible: selecting },
        vertLine: { labelVisible: false, visible: !selecting },
      },
    })
    if (chartType === 'line') instance.priceSeries.applyOptions({ crosshairMarkerVisible: !selecting })
    if (selecting) setTooltip(undefined)
  }, [chartType, onSelectPrice])

  useEffect(() => {
    const instance = chartRef.current
    if (!instance) return
    syncSimplePriceLines(instance.priceSeries, priceLinesRef.current, referenceLines, theme)
  }, [chartType, referenceLines, theme])

  useEffect(() => {
    chartRef.current?.markers.setMarkers(getSimpleExecutionMarkers(attachedMarkers))
  }, [chartType, attachedMarkers])

  return (
    <>
      <styledEl.Canvas ref={containerRef} $canSelectPrice={!!onSelectPrice} />
      {onSelectPrice ? <styledEl.SelectionHint>{t`Click chart to set limit price`}</styledEl.SelectionHint> : null}
      {tooltip && showTooltip && !onSelectPrice ? (
        <PriceChartTooltip data={tooltip} metric={metric} locale={locale} />
      ) : null}
    </>
  )
}
